import React, { useMemo, useCallback, useContext } from "react";
import { Switch } from "antd";
import type { SwitchProps as AntSwitchProps } from "antd";
import { useAndStore } from "../../base/store/useAndStore";
import { getDataSetStoreKey } from "../../base/storeKeyUtils";
import { parseChangeAction, createCommand } from "../../base/actionParser";
import { useFieldValidation } from "../../base/useFieldValidation";
import { ValidateHelp } from "../../base/ValidateHelp";
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from "../../types/common";
import { DataSetInheritContext } from "../../base/DataSetInheritContext";

export interface AndSwitchProps
  extends Omit<AntSwitchProps, "onChange">,
    BaseComponentProps,
    DataSetConfig,
    ValidationDisplayConfig {
  rules?: Array<{ type?: string; required?: boolean; message?: string }>;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  changeAction?: string;
  onChange?: (checked: boolean) => void;
}

export const AndSwitch: React.FC<AndSwitchProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  changeAction,
  onChange,
  checked: propChecked,
  defaultChecked,
  ...restProps
}) => {
  const ctx = useContext(DataSetInheritContext);
  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );
  const storeProps = useAndStore((s) =>
    sessionId && id ? s.propsStore[`${sessionId}_props`]?.[id] ?? null : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && dataSetField ? s.getDataSet(dataSetKey, dataSetField) : undefined
  );

  const { validate, help } = useFieldValidation({
    rules,
    dataSet,
    dataSetField,
    sessionId,
    id,
    develop,
  });

  const displayChecked = useMemo(() => {
    if (storeValue !== undefined) return Boolean(storeValue);
    if (storeProps?.checked !== undefined) return storeProps.checked;
    return propChecked ?? defaultChecked;
  }, [storeValue, storeProps?.checked, propChecked, defaultChecked]);

  const handleChange = useCallback(
    (checked: boolean) => {
      if (onChange) {
        onChange(checked);
        return;
      }
      if (dataSetKey && dataSetField) setDataSet(dataSetKey, dataSetField, checked);
      if (validateOnChange) validate(checked);
      if (changeAction && sessionId) {
        const { actionName, arg } = parseChangeAction(changeAction, checked, {
          id,
          dataSet,
          dataSetField,
        }, ctx);
        addCommand(sessionId, createCommand(actionName, arg));
      }
    },
    [
      onChange,
      dataSetKey,
      dataSetField,
      setDataSet,
      validateOnChange,
      validate,
      changeAction,
      sessionId,
      id,
      addCommand,
      ctx,
    ]
  );

  const handleBlur = useCallback(() => {
    if (!validateOnBlur) return;
    validate(displayChecked);
  }, [validateOnBlur, validate, displayChecked]);

  return (
    <div style={{ display: "inline-block" }} onBlur={handleBlur}>
      <Switch
        {...restProps}
        disabled={develop ? false : restProps.disabled}
        checked={displayChecked}
        onChange={handleChange}
      />
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndSwitch;
